import { ArrayField, useRecordContext, useStore } from "react-admin";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { NiyaazDataGrid } from "../common/NiyaazDataGrid";
import { getNiyaazBookedSlotsConfig } from "../common/niyaaz-booked-slots-config";
import { formatINR } from "@/utils";
import { formatVenueLabel } from "@/utils/event-venue-utils";
import type { CurrentEvent } from "@/containers/events/types";
import {
  NiyaazShowLabeledStack,
  NiyaazShowYesNo,
  type NiyaazShowLabeledItem,
} from "./NiyaazShowField";
import type { NiyaazShowRecord } from "./niyaaz-show-types";

type NiyaazShowInformationTabProps = {
  isAshara: boolean;
};

export const NiyaazShowInformationTab = ({ isAshara }: NiyaazShowInformationTabProps) => {
  const record = useRecordContext<NiyaazShowRecord>();
  const [currentEvent] = useStore<CurrentEvent | null>("currentEvent", null);

  if (!record) return null;

  const payable = Number(record.totalPayable) || 0;
  const paid = Number(record.paidAmount) || 0;
  const balance = record.balance != null ? Number(record.balance) : payable - paid;
  const bookedSlots = record.bookedSlots ?? [];
  const hasComments = Boolean(record.comments?.trim());
  const historyRows = record.previousNiyaazHistory;
  const hasHistory = Array.isArray(historyRows) && historyRows.length > 0;
  const slotsConfig = getNiyaazBookedSlotsConfig(isAshara);

  const hofItems: NiyaazShowLabeledItem[] = [
    ...(record.formNo ? [{ label: "Form No.", value: record.formNo, valueFontWeight: 600 }] : []),
    ...(isAshara
      ? [{ label: "Venue", value: formatVenueLabel(record.markaz, currentEvent) }]
      : [
          { label: "Jaman Venue", value: formatVenueLabel(record.markaz, currentEvent) },
          { label: "Namaaz Venue", value: formatVenueLabel(record.namaazVenue, currentEvent) },
        ]),
    { label: "Name", value: record.HOFName || "—" },
    { label: "ITS No.", value: record.HOFId },
    { label: "Phone", value: record.HOFPhone },
    { label: "Address", value: record.HOFAddress },
    { label: "Send reminders", value: NiyaazShowYesNo(record.sendReminders) },
  ];

  const takhmeenItems: NiyaazShowLabeledItem[] = [
    {
      label: isAshara ? "Niyaaz amount" : "Takhmeen",
      value: formatINR(record.takhmeenAmount),
    },
    ...(!isAshara ? [{ label: "Iftaari", value: formatINR(record.iftaari) }] : []),
    { label: "Zabihat", value: record.zabihat ?? "—" },
    { label: "Chairs", value: record.chairs ?? "—" },
    ...(isAshara ? [{ label: "Mehmaans", value: record.mehmaanCount ?? "—" }] : []),
    ...(isAshara ? [{ label: "Relay", value: NiyaazShowYesNo(record.relayRequired) }] : []),
    { label: "Pirsa required", value: NiyaazShowYesNo(record.pirsaRequired) },
    ...(record.pirsaRequired
      ? [
          { label: "Pirsa", value: record.pirsa ?? "—" },
          {
            label: "Pirsa fulfillment",
            value: record.pirsaSelfPickup ? "Self pickup" : "Delivery needed",
          },
        ]
      : []),
  ];

  const paymentItems: NiyaazShowLabeledItem[] = [
    {
      label: "Total Payable",
      value: formatINR(payable),
      valueVariant: "h5" as const,
      valueFontWeight: 700,
      valueColor: "primary.main",
    },
    {
      label: "Paid Amount",
      value: formatINR(paid),
      valueVariant: "h5" as const,
      valueFontWeight: 700,
    },
    {
      label: "Pending Balance",
      value: formatINR(balance),
      valueVariant: "h5" as const,
      valueFontWeight: 700,
      valueColor: balance > 0 ? "warning.main" : "success.main",
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 3 }}>
        <NiyaazShowLabeledStack items={hofItems} />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <NiyaazShowLabeledStack items={takhmeenItems} rowOnMobile />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <NiyaazShowLabeledStack items={paymentItems} rowOnMobile />
      </Grid>

      <Grid size={12} sx={{ pt: 2, borderTop: 1, borderTopColor: "divider", minWidth: 0 }}>
        {hasHistory ? (
          <ArrayField source="previousNiyaazHistory">
            <NiyaazDataGrid />
          </ArrayField>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No previous takhmeen records
          </Typography>
        )}
      </Grid>

      {hasComments ? (
        <Grid size={12} sx={{ pt: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Comments
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {record.comments}
          </Typography>
        </Grid>
      ) : null}

      {bookedSlots.length > 0 ? (
        <Grid size={12} sx={{ pt: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {slotsConfig.sectionTitle}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{slotsConfig.dayColumnHeader}</TableCell>
                  <TableCell>Slot</TableCell>
                  <TableCell>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookedSlots.map((slot, index) => (
                  <TableRow key={index}>
                    <TableCell>{slotsConfig.getDayLabel(slot.day)}</TableCell>
                    <TableCell>
                      <Chip label={slotsConfig.slotLabels[slot.slot] ?? slot.slot} size="small" />
                    </TableCell>
                    <TableCell>{slot.type}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      ) : null}
    </Grid>
  );
};
